import { useState, useEffect } from 'react';
import {
  Box,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Alert,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { supabase } from '../../../services/supabase';
import { SimulationReport, type Diagnostics, type FeedStory } from './RecSimulation';

type Mode = 'simulation' | 'short' | 'long';

interface UserRecommendationsTabProps {
  userId: string;
}

export const UserRecommendationsTab = ({
  userId,
}: UserRecommendationsTabProps) => {
  const [activeMode, setActiveMode] = useState<Mode | null>(null);
  const [runKey, setRunKey] = useState(0);

  const handleModeClick = (_: React.MouseEvent, mode: Mode | null) => {
    if (mode === null) return;
    setActiveMode(mode);
    setRunKey(k => k + 1);
  };

  return (
    <Box>
      <ToggleButtonGroup
        value={activeMode}
        exclusive
        onChange={handleModeClick}
        size="small"
      >
        <ToggleButton value="simulation">Stream Simulation</ToggleButton>
        <ToggleButton value="short">Short-term Similarity</ToggleButton>
        <ToggleButton value="long">Long-term Similarity</ToggleButton>
      </ToggleButtonGroup>

      <Box sx={{ mt: 2 }}>
        {activeMode === 'simulation' && (
          <SimulationPanel key={`sim-${runKey}`} userId={userId} />
        )}
        {activeMode === 'short' && (
          <SimilarityPanel key={`short-${runKey}`} userId={userId} source="short" />
        )}
        {activeMode === 'long' && (
          <SimilarityPanel key={`long-${runKey}`} userId={userId} source="long" />
        )}
      </Box>
    </Box>
  );
};

// --- Simulation panel (auto-fetches on mount) ---

const SimulationPanel = ({ userId }: { userId: string }) => {
  const [diagnostics, setDiagnostics] = useState<Diagnostics | null>(null);
  const [feedStories, setFeedStories] = useState<FeedStory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const { data, error: fnError } = await supabase.functions.invoke(
          'get-recommendations',
          { body: { userId, lang: 'en', limit: 10, debug: true } }
        );
        if (cancelled) return;
        if (fnError) throw new Error(fnError.message);
        if (!data?.success) throw new Error(data?.error ?? 'Pipeline failed');
        if (!data.result?.diagnostics)
          throw new Error('No diagnostics returned');

        // Build showSlug lookup from diagnostics scoring
        const showSlugMap = new Map<string, string>(
          (data.result.diagnostics.scoring ?? []).map(
            (s: { slug: string; showSlug: string }) => [s.slug, s.showSlug]
          )
        );

        const stories: FeedStory[] = (data.result.stories ?? []).map(
          (s: { slug: string; reason: string; score?: number; clusterId?: string | null; similarityBand?: string }) => ({
            slug: s.slug,
            showSlug: showSlugMap.get(s.slug),
            reason: s.reason,
            score: s.score ?? 0,
            clusterId: s.clusterId,
            similarityBand: s.similarityBand,
          })
        );

        // Enrich stories with titles, show names, and cluster descriptions
        if (stories.length > 0) {
          const slugs = stories.map(s => s.slug);
          const clusterIds = [...new Set(stories.map(s => s.clusterId).filter(Boolean))] as string[];

          // Fetch story details from stories_public (title + show_name)
          const storyDetailsPromise = supabase
            .from('stories_public')
            .select('slug, title, show_name')
            .in('slug', slugs)
            .eq('lang', 'en');

          // Fetch cluster descriptions
          const clusterPromise = clusterIds.length > 0
            ? supabase.from('clusters').select('id, description').in('id', clusterIds)
            : Promise.resolve({ data: [] as { id: string; description: string | null }[] });

          const [{ data: storyDetails }, { data: clusters }] = await Promise.all([
            storyDetailsPromise,
            clusterPromise,
          ]);

          if (!cancelled) {
            if (storyDetails) {
              const detailMap = new Map(
                storyDetails.map(d => [d.slug, d])
              );
              for (const s of stories) {
                const detail = detailMap.get(s.slug);
                if (detail) {
                  s.title = detail.title;
                  s.showName = detail.show_name;
                }
              }
            }
            if (clusters) {
              const clusterMap = new Map(
                clusters.map(c => [c.id, c.description])
              );
              for (const s of stories) {
                if (s.clusterId) {
                  s.clusterDescription = clusterMap.get(s.clusterId);
                }
              }
            }
          }
        }

        if (!cancelled) {
          setDiagnostics(data.result.diagnostics);
          setFeedStories(stories);
        }
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">
          Running simulation...
        </Typography>
      </Box>
    );
  }
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!diagnostics) return null;
  return <SimulationReport diagnostics={diagnostics} feedStories={feedStories} />;
};

// --- Similarity panel (auto-fetches on mount) ---

interface SimilarityStory {
  slug: string;
  showSlug: string;
  similarity: number;
}

interface SimilarityResult {
  stories: SimilarityStory[];
  embeddingSource: 'short' | 'long';
  hasEmbedding: boolean;
}

const SimilarityPanel = ({
  userId,
  source,
}: {
  userId: string;
  source: 'short' | 'long';
}) => {
  const [result, setResult] = useState<SimilarityResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const { data, error: fnError } = await supabase.functions.invoke(
          'get-recommendations',
          {
            body: {
              userId,
              lang: 'en',
              limit: 20,
              mode: 'similarity',
              embeddingSource: source,
            },
          }
        );
        if (cancelled) return;
        if (fnError) throw new Error(fnError.message);
        if (!data?.success) throw new Error(data?.error ?? 'Query failed');
        setResult(data.result);
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [userId, source]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">
          Querying {source}-term similarity...
        </Typography>
      </Box>
    );
  }
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!result) return null;

  if (!result.hasEmbedding) {
    return (
      <Typography variant="caption" color="text.secondary">
        No {source}-term embedding for this user.
      </Typography>
    );
  }

  if (result.stories.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        No matching stories found.
      </Typography>
    );
  }

  return (
    <TableContainer sx={{ maxHeight: 400 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }}>#</TableCell>
            <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }}>Slug</TableCell>
            <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }}>Show</TableCell>
            <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }} align="right">
              Similarity
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {result.stories.map((story, i) => (
            <TableRow key={story.slug}>
              <TableCell
                sx={{ fontSize: '0.7rem', py: 0.25, color: 'text.secondary' }}
              >
                {i + 1}
              </TableCell>
              <TableCell
                sx={{ fontSize: '0.7rem', py: 0.25, fontFamily: 'monospace' }}
              >
                {story.slug}
              </TableCell>
              <TableCell sx={{ fontSize: '0.7rem', py: 0.25 }}>
                {story.showSlug}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontSize: '0.7rem',
                  py: 0.25,
                  fontFamily: 'monospace',
                  fontWeight: story.similarity >= 0.9 ? 'bold' : undefined,
                  color: story.similarity >= 0.9 ? 'success.main' : undefined,
                }}
              >
                {story.similarity}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
